/** Translation keys of one question, relative to the page's Transloco prefix. */
export interface FaqEntry {
  readonly question: string;
  readonly answer: string;
}

/** FAQ shown on a public page; its template and the FAQPage JSON-LD both read these entries. */
export interface PageFaq {
  /** Transloco prefix of the page template, e.g. `anc.vps`. */
  readonly prefix: string;
  readonly entries: readonly FaqEntry[];
}

const entries = (base: string, keys: readonly string[], question = 'question', answer = 'answer'): FaqEntry[] =>
  keys.map((key) => ({ question: `${base}.${key}.${question}`, answer: `${base}.${key}.${answer}` }));

const VPS: PageFaq = {
  prefix: 'anc.vps',
  entries: entries('faq', ['choice', 'ddos', 'console', 'location'], 'q', 'a'),
};

/** Keyed by public route path ('' is the home page), as in public-routes.json. */
export const PAGE_FAQ = {
  '': { prefix: 'anc.home', entries: entries('faq.items', ['location', 'cloudAct', 'migration', 'commitment']) },
  'vps-standard': VPS,
  'vps-performance': VPS,
  plesk: { prefix: 'anc.plesk', entries: entries('faq', ['vps', 'ssl', 'backups', 'sites'], 'q', 'a') },
  'plesk-dedicated': {
    prefix: 'anc.pleskDedicated',
    entries: entries('faq', ['licence', 'root', 'order', 'ddos'], 'q', 'a'),
  },
  domains: {
    prefix: 'anc.domains',
    entries: ['activation', 'transfer', 'whois'].map((key) => ({ question: `faq.${key}Q`, answer: `faq.${key}A` })),
  },
  datacenter: {
    prefix: 'anc.datacenter',
    entries: entries('faq.items', ['location', 'visit', 'access', 'pricing'], 'q', 'a'),
  },
  'ip-tunnels': {
    prefix: 'anc.ipTunnels',
    entries: entries('faq.items', ['protocol', 'encryption', 'prefixes', 'activation']),
  },
  antiddos: { prefix: 'anc.antiddos', entries: entries('faq', ['price', 'app', 'gaming', 'activate']) },
  dedicated: {
    prefix: 'anc.dedicated',
    entries: entries('faq', ['order', 'custom', 'location', 'migration'], 'q', 'a'),
  },
  ssl: { prefix: 'anc.ssl', entries: entries('faq.items', ['free', 'renewal', 'wildcard', 'company']) },
  fibre: { prefix: 'anc.fibre', entries: entries('faq.items', ['eligibility', 'ip', 'outage', 'multisite']) },
  telephonie: { prefix: 'anc.telephonie', entries: entries('faq.items', ['numbers', 'plans', 'equipment']) },
  workspace: { prefix: 'anc.workspace', entries: entries('faq.items', ['billing', 'office', 'storage']) },
  'guide-vps': { prefix: 'anc.guides', entries: entries('vps.faq.items', ['docker', 'windows', 'backups']) },
  'guide-plesk': { prefix: 'anc.guides', entries: entries('plesk.faq.items', ['backups', 'licence', 'admin']) },
  'guide-antiddos': {
    prefix: 'anc.guides',
    entries: entries('antiddos.faq.items', ['same', 'activate', 'application']),
  },
  'guide-colocation': { prefix: 'anc.guides', entries: entries('colocation.faq.items', ['price', 'ip', 'custom']) },
} as const satisfies Readonly<Record<string, PageFaq>>;

export function pageFaq(path: string): PageFaq | undefined {
  return Object.hasOwn(PAGE_FAQ, path) ? PAGE_FAQ[path as keyof typeof PAGE_FAQ] : undefined;
}
